import { ReactNode, useCallback, useEffect, useState } from 'react'
import { Link, usePage } from '@inertiajs/react'
import {
  Cable,
  FileCode2,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Plug,
  Search,
  Workflow,
} from 'lucide-react'
import FlashMessages from '@/frontend/components/FlashMessages'
import UndoToastHost from '@/frontend/components/undo/UndoToastHost'
import CommandPalette, { openCommandPalette } from '@/frontend/components/CommandPalette'
import type { SharedProps } from '@/frontend/types/inertia'
import OrgAccountMenu from '@/frontend/layouts/worker-first/OrgAccountMenu'
import AgentsSection from '@/frontend/layouts/worker-first/AgentsSection'
import { useNewConversation } from '@/frontend/layouts/worker-first/useNewConversation'
import ForgeMenu from '@/frontend/features/ForgeNavigation/ForgeMenu'
import ProjectNavigation from '@/frontend/features/ForgeNavigation/ProjectNavigation'
import ContextHeader from '@/frontend/features/ForgeNavigation/ContextHeader'
import SectionTabs from '@/frontend/features/ForgeNavigation/SectionTabs'
import DashboardCatalogLink from '@/frontend/features/DashboardRuntime/CatalogLink'
import { dashboardViewportClassName, dashboardContentOverflowClassName } from '@/frontend/layouts/dashboardViewport'

interface Props {
  children: ReactNode;
  variant?: 'default' | 'fullscreen' | 'canvas';
  contentRailOpen?: boolean;
}

/**
 * Worker-first navigation shell (SZ-1558, behind featureFlags.worker_first_nav_enabled).
 * Organization and worker pages retain the global menu. Forge routes replace
 * it with project destinations and a single scrolling region, while agents
 * and the account anchor remain available. The legacy layout is unchanged.
 */
export default function WorkerFirstSidebar({ children, variant = 'default', contentRailOpen = false }: Props) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [fullscreenSidebarCollapsed, setFullscreenSidebarCollapsed] = useState(() => (
    typeof window !== 'undefined' && window.localStorage.getItem('wfn:fullscreen-sidebar-collapsed') === 'true'
  ))
  const { props: pageProps, url } = usePage<SharedProps>()
  const pendingApprovalsCount = pageProps.pendingApprovalsCount ?? 0

  const [path] = url.split('?')
  const onWorkerChatPage = /^\/dashboard\/digital_workers\/[^/]+\/chat$/.test(path)
  const onWorkerMemoryPage = /^\/dashboard\/digital_workers\/[^/]+\/memoria$/.test(path)
  const onBusinessProcessCreatePage = path === '/dashboard/business_processes/new'
  const onWideWorkerPage = onWorkerChatPage || onWorkerMemoryPage || onBusinessProcessCreatePage
  // Itens org da sidebar (SZ subiu do dropdown, Iteração 3): active-state por
  // path. Integrações também casa as páginas de detalhe do Composio.
  const onIntegrationsPage = path.startsWith('/dashboard/integrations') || path.startsWith('/dashboard/evolution_accounts') ||
    path.startsWith('/dashboard/composio') || path === '/dashboard/credentials'
  const onWorkflowsPage = path.startsWith('/dashboard/workflows')
  const onArtifactsPage = path === '/dashboard/artifacts'
  const dashboardProjectId = path.match(/^\/dashboard\/projects\/([^/]+)\/dashboards(?:\/|$)/)?.[1]
  const onDiscoveryPage = path.startsWith('/dashboard/discovery') || !!dashboardProjectId ||
    (path.startsWith('/dashboard/workflows') && new URLSearchParams(url.split('?')[1] || '').has('project_id'))
  const canAccessForge = pageProps.permissions?.can_access_forge === true
  const forgeRestricted = !canAccessForge && pageProps.permissions?.access_scope === 'forge_only'

  const closeMobileMenu = useCallback(() => setIsMobileMenuOpen(false), [])
  const toggleFullscreenSidebar = useCallback(() => {
    setFullscreenSidebarCollapsed((collapsed) => {
      window.localStorage.setItem('wfn:fullscreen-sidebar-collapsed', String(!collapsed))
      return !collapsed
    })
  }, [])

  // A ação primária do shell + seu atalho global ⌘N/Ctrl+N: inicia uma
  // conversa nova com o agente-alvo (igual ao "Conversar" da home).
  const { startNewConversation } = useNewConversation(closeMobileMenu)

  // Lock body scroll while the mobile drawer is open (same behavior as the legacy sidebar)
  useEffect(() => {
    if (!isMobileMenuOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isMobileMenuOpen])

  const menuItemClass = (active: boolean) =>
    `flex items-center gap-2 rounded-[10px] px-3 py-2 text-[13.5px] font-medium transition-colors duration-150 ${
      active ? 'bg-accent text-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground'
    }`

  return (
    <>
      <CommandPalette />
      {/* text-sm no wrapper (SZ-1587): corpo 14px só no shell do dashboard, por
          herança — marketing/auth/checkout ficam no 16px default. Tamanhos
          explícitos (text-xs, text-2xl…) não são afetados por serem rem-based. */}
      <div className={dashboardViewportClassName(variant)}>
        {/* Mobile Header */}
        <div className="md:hidden shrink-0 bg-background p-4 flex items-center justify-between border-b border-border">
          <Link href="/dashboard" aria-label="Ir para o início" className="rounded-md transition-opacity duration-150 hover:opacity-75">
            <img src="/landing/images/sz-orb-logo.png" alt="StationZero" className="h-5 object-contain dark:hidden" />
            <img src="/landing/images/sz-orb-logo-white.png" alt="" className="h-5 object-contain hidden dark:block" />
          </Link>
          {!isMobileMenuOpen && !forgeRestricted && (
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="p-1.5 rounded-lg text-muted-foreground hover:text-accent-foreground hover:bg-accent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring"
              aria-label="Abrir menu"
            >
              <Menu className="h-5 w-5" strokeWidth={1.75} />
            </button>
          )}
        </div>

        {/* Sidebar */}
        <aside
          data-testid="wfn-sidebar"
          className={`
            ${isMobileMenuOpen ? 'flex fixed' : 'hidden'}
            md:flex md:flex-shrink-0
            flex-col inset-y-0 left-0 h-full md:h-auto w-64
            bg-sidebar border-r border-border
            z-50 transition-[width,opacity] duration-200 ease-out
            ${variant !== 'default' && fullscreenSidebarCollapsed ? 'md:w-0 md:overflow-hidden md:border-r-0 md:opacity-0 md:pointer-events-none' : 'md:w-64'}
          `}
        >
          {/* Topo do protótipo: orb 18px + "StationZero" 13/600 + kbd ⌘K à
              direita (entrada discreta na CommandPalette, B.2). O logo é a
              affordance permanente de Início — o item "Início" saiu na
              Iteração 2 (o logo cobre). O badge "Precisa de você"
              (SZ-1599/C.4) segue aqui atrás da flag approval_queue_enabled;
              só aparece com pendências. */}
          <div className="flex items-center gap-2 border-b border-border px-3.5 pb-3 pt-2.5 md:border-b-0">
            <Link
              href="/dashboard"
              onClick={closeMobileMenu}
              aria-label="Ir para o início"
              data-testid="wfn-logo-home"
              className="flex items-center gap-2 rounded-md transition-opacity duration-150 hover:opacity-75"
            >
              {/* Logo inteira (orb + wordmark), igual à produção — não mais
                  orb + texto separados. Variante branca no dark. */}
              <img src="/landing/images/sz-orb-logo.png" alt="StationZero" className="h-5 object-contain dark:hidden" />
              <img src="/landing/images/sz-orb-logo-white.png" alt="StationZero" aria-hidden className="h-5 object-contain hidden dark:block" />
            </Link>
            <span className="ml-auto flex items-center gap-1">
              {pendingApprovalsCount > 0 && (
                <Link
                  href="/dashboard"
                  onClick={closeMobileMenu}
                  data-testid="wfn-approvals-badge"
                  aria-label={pendingApprovalsCount === 1
                    ? '1 item precisa de você'
                    : `${pendingApprovalsCount} itens precisam de você`}
                  title="Precisa de você"
                  className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-warning/13 px-1.5 text-[10.5px] font-semibold leading-none text-warning transition-opacity duration-150 hover:opacity-80 dark:bg-warning/15"
                >
                  {pendingApprovalsCount}
                </Link>
              )}
              <button
                type="button"
                onClick={() => { closeMobileMenu(); openCommandPalette() }}
                data-testid="wfn-command-palette"
                aria-label="Busca e navegação rápida (⌘K)"
                title="Busca e navegação rápida (⌘K)"
                className="flex items-center rounded-md text-muted-foreground transition-colors duration-150 hover:text-foreground"
              >
                <Search className="h-4 w-4 md:hidden" strokeWidth={1.75} />
                <kbd className="hidden h-[18px] items-center rounded-[5px] border border-border px-[5px] font-mono text-[10px] text-faint-foreground md:inline-flex">⌘K</kbd>
              </button>
            </span>
          </div>

          {/* Menu fixo (Iteração 2, org-level na Iteração 3): itens simples
              estilo Codex. "Nova conversa" INICIA uma conversa com o
              agente-alvo (⌘N; a home não tem mais composer). Abaixo, a
              navegação da ORGANIZAÇÃO que subiu do dropdown (Iteração 3):
              Integrações · Publicações — as páginas org-level de acesso
              frequente saem do rodapé para a coluna. Credenciais fica como
              aba interna de Integrações. Sem "Início" (logo cobre) e sem
              porta de Painel. A seção Agentes abaixo reúne contratação e
              links do portal. */}
          {onDiscoveryPage && canAccessForge ? <div data-forge-scroll className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            <ProjectNavigation onNavigate={closeMobileMenu} />
            <AgentsSection compact mobileMenuOpen={isMobileMenuOpen} onNavigate={closeMobileMenu} />
          </div> : <><div className="shrink-0 space-y-0.5 px-2.5 pt-1">
            {!forgeRestricted && <button
              type="button"
              onClick={startNewConversation}
              data-testid="wfn-new-conversation"
              title="Nova conversa (⌘N)"
              className={`w-full text-left ${menuItemClass(false)}`}
            >
              <Plus className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
              <span className="flex-1">Nova conversa</span>
              <kbd className="hidden h-[18px] items-center rounded-[5px] border border-border px-[5px] font-mono text-[10px] font-normal text-faint-foreground md:inline-flex">⌘N</kbd>
            </button>}
            {!forgeRestricted && <Link
              href="/dashboard/integrations"
              onClick={closeMobileMenu}
              aria-current={onIntegrationsPage ? 'page' : undefined}
              data-testid="wfn-integrations-link"
              className={menuItemClass(onIntegrationsPage)}
            >
              <Cable className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
              Integrações
            </Link>}
            {pageProps.permissions?.can_access_workflows && <Link
              href="/dashboard/workflows"
              onClick={closeMobileMenu}
              aria-current={onWorkflowsPage ? 'page' : undefined}
              data-testid="wfn-workflows-link"
              className={menuItemClass(onWorkflowsPage)}
            >
              <Workflow className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
              {pageProps.navigationLabels.workflows}
            </Link>}
            {!canAccessForge && <DashboardCatalogLink onNavigate={closeMobileMenu} />}
            {canAccessForge && <ForgeMenu active={onDiscoveryPage} onNavigate={closeMobileMenu} />}
            {!forgeRestricted && <Link
              href="/dashboard/artifacts"
              onClick={closeMobileMenu}
              aria-current={onArtifactsPage ? 'page' : undefined}
              data-testid="wfn-artifacts-link"
              className={menuItemClass(onArtifactsPage)}
            >
              <FileCode2 className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
              Publicações
            </Link>}
          </div>

          {!forgeRestricted && <AgentsSection
            mobileMenuOpen={isMobileMenuOpen}
            onNavigate={closeMobileMenu}
          />}</>}

          {pageProps.auth.user?.is_admin && <Link href="/cc/mcp_accesses" onClick={closeMobileMenu}
            aria-label={pageProps.navigationLabels.local_agents} title={pageProps.navigationLabels.local_agents}
            data-testid="wfn-mcp-link" className="mx-2.5 mb-2 flex shrink-0 items-center gap-2 rounded-md px-2.5 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-foreground">
            <Plug className="size-4 shrink-0" strokeWidth={1.75} />{pageProps.navigationLabels.local_agents}
          </Link>}

          {/* Footer org/account anchor (SZ-1571): org identity + full global menu */}
          <OrgAccountMenu onNavigate={closeMobileMenu} />
        </aside>

        {variant !== 'default' && (
          <button
            type="button"
            onClick={toggleFullscreenSidebar}
            data-testid="wfn-fullscreen-sidebar-toggle"
            aria-label={fullscreenSidebarCollapsed ? pageProps.navigationLabels.expand_sidebar : pageProps.navigationLabels.collapse_sidebar}
            title={fullscreenSidebarCollapsed ? pageProps.navigationLabels.expand_sidebar : pageProps.navigationLabels.collapse_sidebar}
            className={`fixed bottom-4 z-[60] hidden size-8 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground shadow-sm transition-[left,color] duration-200 hover:text-foreground md:flex ${
              fullscreenSidebarCollapsed ? 'left-2' : 'left-[15rem]'
            }`}
          >
            {fullscreenSidebarCollapsed ? <PanelLeftOpen className="size-4" /> : <PanelLeftClose className="size-4" />}
          </button>
        )}

        {/* Main Content (overflow-x-clip: o canvas dither da home é mais largo
            que a coluna e não deve criar scroll horizontal).
            scrollbar-gutter:stable reserva o espaço da barra de rolagem sempre,
            para o conteúdo centralizado (max-w-5xl mx-auto) não deslocar ~15px
            ao trocar de aba (abas curtas sem scroll × abas longas com scroll). */}
        <div
          data-content-rail-state={contentRailOpen ? 'open' : 'closed'}
          className={`flex min-w-0 flex-1 flex-col overflow-x-clip transition-[margin] duration-200 ease-out motion-reduce:transition-none ${
          contentRailOpen ? 'lg:mr-[480px]' : ''
        } ${
          dashboardContentOverflowClassName(variant, onWorkerMemoryPage)
        } ${variant === 'default' && !onWorkerMemoryPage ? 'md:[scrollbar-gutter:stable]' : ''
        }`}
        >
          <FlashMessages />
          <UndoToastHost />
          {onDiscoveryPage && canAccessForge && <><ContextHeader /><SectionTabs /></>}
          {variant !== 'default' ? (
            <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
              {children}
            </div>
          ) : (
            <div
              className={`flex min-w-0 flex-1 flex-col ${onWorkerChatPage || onBusinessProcessCreatePage ? 'p-0' : 'p-4 md:p-8'} ${
                onWideWorkerPage ? 'min-h-0' : ''
              }`}
            >
              {/* Chat and memory use the viewport width; other dashboard pages
                  keep the canonical portal width. */}
              <div
                data-dashboard-content-width={onWideWorkerPage ? 'wide' : 'standard'}
                className={`min-w-0 flex-1 w-full mx-auto transition-[max-width] duration-500 ease-out motion-reduce:transition-none ${
                  onWideWorkerPage ? 'flex min-h-0 flex-col' : ''
                } ${onWideWorkerPage ? 'max-w-[100%]' : 'max-w-5xl'}`}
              >
                {children}
              </div>
            </div>
          )}
        </div>

        {/* Mobile menu backdrop overlay */}
        {isMobileMenuOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black/20 z-40"
            onClick={closeMobileMenu}
          />
        )}

      </div>
    </>
  )
}
